import { Badge } from '@/components/ui/badge'
import type { PendingSurvey, Question, TemplateQuestion } from '@/api/orderDetails'
import { useLocalization } from '@/contexts/LocalizationContext'

function getQuestionLabel(question: Question | null | undefined, locale: 'en' | 'ar'): string {
  if (!question) return ''
  if (locale === 'ar') {
    return question.textAr ?? question.textEn ?? question.code
  }
  return question.textEn ?? question.textAr ?? question.code
}

export function PendingSurveyCard({ survey }: { survey: PendingSurvey }) {
  const { t, locale } = useLocalization()
  const questions = survey.survey?.templateQuestions ?? []
  const questionLabels = questions
    .map((tq: TemplateQuestion) => getQuestionLabel(tq.question, locale))
    .filter(Boolean)

  return (
    <div className="rounded-lg border border-yellow-200 bg-yellow-50/80 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h4 className="font-medium text-gray-900">{survey.surveyTitle}</h4>
          {survey.survey?.description && (
            <p className="text-sm text-gray-600 mt-1">{survey.survey.description}</p>
          )}
        </div>
        <Badge
          className={
            survey.isRequired
              ? 'bg-red-100 text-red-700 border-0 shrink-0'
              : 'bg-gray-100 text-gray-700 border-0 shrink-0'
          }
        >
          {survey.isRequired ? t('orderDetails.required') : t('orderDetails.optional')}
        </Badge>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-gray-600">
        <span>
          {questions.length} {t('orderDetails.surveyQuestionsLabel')}
        </span>
        {questionLabels.length > 0 && (
          <span className="text-xs text-gray-500 whitespace-pre-line block w-full">
            {questionLabels.join('\n')}
          </span>
        )}
      </div>
    </div>
  )
}
